/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: completed-deliveries-component.ts
 *
 * Created by randysorensen on 2/20/18.
 */

function CompletedDeliveriesController(this: JS5Object,
                                       _: JS5Object,
                                       $q: JS5Object,
                                       $filter: JS5Function,
                                       EndPointService: JS5Object,
                                       AuthFactory: JS5Object,
                                       PermissionsService: JS5Object,
                                       PackageService: JS5Object,
                                       UtilsService: JS5Object) {

    //================//
    // Template Logic //
    //================//

    const getMasterMetadataURL = (job: JS5Object): string => {
        return EndPointService.reportEndpoint + '/masterMetadataXml/' + job.id;
    };

    const getPartnerMetadataURL = (file: JS5Object): string => {
        return EndPointService.reportEndpoint + '/partnerMetadataXml/' + file.id;
    };

    const getPartnerProxyURL = (file: JS5Object): string => {
        return EndPointService.mediaAssetEndpoint + '?contentId=' + file.proxyId;
    };

    const getPartnerQCReportURL = (file: JS5Object): string => {
        return EndPointService.mediaAssetEndpoint + '?contentId=' + file.qcReportId;
    };

    const getAuthenticatedURL = (link: string): string => {
        let delimiter = _.includes(link, '?') ? '&' : '?';
        return link + delimiter + AuthFactory.getAuthQueryString();
    };

    this.downloadFile = (url: string) => {
        window.open(url);
    };


    //======//
    // Grid //
    //======//

    this.gridOptions = {
        enableSorting: false,
        enableFiltering: false,
        enableColumnMenus: false,
        showTreeExpandNoChildren: false,
        treeRowHeaderBaseWidth: 12,
        rowTemplate: 'package/editor/components/shared/completedDeliveries/gridTemplates/delivery-row-template.html',
        data: [],
        columnDefs: [

            //
            // The "Delivery" column serves a few purposes.
            //
            // The top level of each tree node in the modal is
            // used for each partner that the package has been
            // delivered to in the past.
            //
            // When expanding this top-level tree node, the first
            // "level" is a date/time on which a full delivery to
            // this partner was completed. It is indented 12
            // pixels.
            //
            // When expanding that, each file asset that was
            // delivered to the partner in that partner delivery
            // workflow is shown. This column shows that file's
            // completion date/time. It is indented 24 pixels.
            //

            {
                name: 'delivery',
                displayName: 'Delivery',
                width: '27%',
                cellTemplate: 'package/editor/components/shared/completedDeliveries/gridTemplates/delivery-cell-template.html',
            },
            {
                name: 'metadata',
                displayName: 'Meta',
                width: '6%',
                cellTemplate: 'components/visual/grid/text-file-download-template.html',
            },
            {
                name: 'proxy',
                displayName: 'Proxy',
                width: '6%',
                cellTemplate: 'components/visual/grid/video-file-download-template.html',
            },
            {
                name: 'qcReport',
                displayName: 'QC',
                width: '6%',
                cellTemplate: 'components/visual/grid/pdf-file-download-template.html',
            },
            {
                name: 'fileAsset',
                displayName: 'File/Asset',
                width: '*',
                cellTemplate: 'components/visual/grid/read-only-text-input-template.html'
            },
            {
                name: 'preDeliveryValidationFlag',
                displayName: 'Validator (Pre-Delivery)',
                width: '6%',
                cellTemplate: 'components/visual/grid/predelivery-validate-icon-template.html',
            },
            {
                name: 'preDeliveryApprovalFlag',
                displayName: 'QC Approval (Pre-Delivery)',
                width: '6%',
                cellTemplate: 'components/visual/grid/predelivery-approval-icon-template.html',
            }
        ],
    };


    //======//
    // Data //
    //======//

    const formatDate = (dateStr: string): string => {
        return $filter('amDateFormat')(dateStr, 'MMMM DD, YYYY, h:mm A');
    };

    const completionDateSortFunction = (event0: JS5Object, event1: JS5Object): number => {
        const completionDate0 = _.get(event0, 'completionDate');
        const completionDate1 = _.get(event1, 'completionDate');
        return UtilsService.dateCompare(completionDate0, completionDate1);
    };

    const partnerNameSortFunction = (delivery0: JS5Object, delivery1: JS5Object): number => {
        const partnerName0 = _.get(delivery0, 'partnerName');
        const partnerName1 = _.get(delivery1, 'partnerName');
        return UtilsService.caseInsensitiveStringCompare(partnerName0, partnerName1);
    };

    const addDeliveredFile = (file: JS5Object, treeLevel: number) => {
        this.gridOptions.data.push({
            delivery: formatDate(file.completionDate),
            isFile: true,
            fileAsset: file.fileName,
            metadata: file.metadataAvailable ? getAuthenticatedURL(getPartnerMetadataURL(file)) : '',
            proxy: file.proxyId ? getAuthenticatedURL(getPartnerProxyURL(file)) : '',
            qcReport: file.qcReportId ? getAuthenticatedURL(getPartnerQCReportURL(file)) : '',
            preDeliveryValidationFlag: file.preDeliveryValidationFlag,
            preDeliveryApprovalFlag: file.preDeliveryApprovalFlag,
            $$treeLevel: treeLevel,
        });
    };

    const addDeliveryJob = (job: JS5Object, treeLevel: number) => {
        this.gridOptions.data.push({
            delivery: formatDate(job.completionDate),
            isJob: true,
            metadata: this.adminFlag && job.metadataAvailable ? getAuthenticatedURL(getMasterMetadataURL(job)) : '',
            $$treeLevel: treeLevel,
        });

        job.filesDelivered.sort(completionDateSortFunction);
        _.forEach(job.filesDelivered, (deliveredFile: JS5Object) => {
            addDeliveredFile(deliveredFile, treeLevel + 1);
        });
    };

    const addDelivery = (delivery: JS5Object, treeLevel: number) => {
        this.gridOptions.data.push({
            delivery: delivery.partnerName,
            top: treeLevel === 0,
            $$treeLevel: treeLevel,
        });

        if (delivery.childDeliveries) {
            delivery.childDeliveries.sort(partnerNameSortFunction);
            _.forEach(delivery.childDeliveries, (childDelivery: JS5Object) => {
                addDelivery(childDelivery, treeLevel + 1);
            });
        } else {
            delivery.deliveryJobs.sort(completionDateSortFunction);
            _.forEach(delivery.deliveryJobs, (childDelivery: JS5Object) => {
                addDeliveryJob(childDelivery, treeLevel + 1);
            });
        }
    };

    const getSuperPartnerNames = (completedDeliveries: JS5Object[]): string[] => {
        const superPartners = _.chain(completedDeliveries)
            .filter((delivery: JS5Object) => !!delivery.parentPartnerName)
            .map('parentPartnerName')
            .uniq()
            .value();

        return superPartners;
    };

    const createSuperPartnerDelivery = (superPartnerName: string, completedDeliveries: JS5Object[]): JS5Object => {
        let childDeliveries = _.remove(
            completedDeliveries,
            (completedDelivery: JS5Object) => completedDelivery.parentPartnerName === superPartnerName);

        let superPartnerDelivery = {
            partnerName: superPartnerName,
            childDeliveries: childDeliveries
        };

        return superPartnerDelivery;
    };

    const getSuperPartnerDeliveries = (completedDeliveries: JS5Object[]): JS5Object[] => {
        const superPartnerNames = getSuperPartnerNames(completedDeliveries);

        let superPartnerDeliveries: JS5Object[] = [];
        _.forEach(superPartnerNames, (superPartnerName: string) => {
            let superPartnerDelivery = createSuperPartnerDelivery(superPartnerName, completedDeliveries);
            superPartnerDeliveries.push(superPartnerDelivery);
        });

        return superPartnerDeliveries;
    };

    const processCompletedDeliveries = (completedDeliveries: JS5Object[]) => {
        let superPartnerDeliveries = getSuperPartnerDeliveries(completedDeliveries);

        completedDeliveries = _.concat(completedDeliveries, superPartnerDeliveries);
        completedDeliveries.sort(partnerNameSortFunction);

        _.forEach(completedDeliveries, (completedDelivery: JS5Object) => {
            addDelivery(completedDelivery, 0);
        });
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        _.set(this, 'loadingIndicator.show', true);
        this.adminFlag = PermissionsService.isAdmin();

        //
        // Admin flag is used to enable download links for
        // post-delivery processing artifacts, metadata and
        // possibly other items - it is _not_ used to permission
        // the completed deliveries data itself
        //

        $q.all([
            // get completed deliveries list
            PackageService.getCompletedDeliveries(this.packageObject).then((completedDeliveries: JS5Object[]) => {
                processCompletedDeliveries(completedDeliveries);
                this.completedDeliveries = completedDeliveries;
            })
        ]).finally(() => {
            _.set(this, 'loadingIndicator.show', false);
        });
    };
}

angular
    .module('prism-ui.package')
    .component('completedDeliveries', {
        controller: CompletedDeliveriesController,
        templateUrl: 'package/editor/components/shared/completedDeliveries/completed-deliveries-template.html',
        bindings: {
            packageObject: '=',
            loadingIndicator: '<'
        }
    });
